/**
 * The technician's own reads (FM Technician rebuild, R1): GET /tech/checkin
 * and the site zones the screens label their times with.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet, unwrapList } from '@/utils/api';

export interface TechSite { id: string; name: string; assembly_point: string | null; timezone?: string | null }
export interface OpenCheckin {
  id: string; building_id: string; site_name: string | null; shift_date: string;
  checked_in_at: string; checked_out_at: string | null; method: 'location' | 'qr';
  toolbox_talk: boolean; client_at: string | null;
}
export interface CheckinState { open: OpenCheckin | null; sites: TechSite[] }

export const CHECKIN_KEY = ['tech', 'checkin'] as const;

/** GET /tech/checkin — maintenance:work. `enabled` is the caller's gate. */
export function useCheckin(enabled: boolean) {
  return useQuery<CheckinState>({
    queryKey: CHECKIN_KEY,
    queryFn: () => apiGet<CheckinState>('/tech/checkin'),
    enabled,
    staleTime: 30_000,
  });
}

/** Site id → IANA zone, from GET /space/buildings (space:view), so a time is
 *  entered and shown on the SITE's clock with its zone label. */
export function useSiteZones(enabled: boolean) {
  return useQuery<Record<string, string | null>>({
    queryKey: ['tech', 'site-zones'],
    enabled,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const rows = unwrapList<{ id: string; timezone?: string | null }>(
        await apiGet<unknown>('/space/buildings?page=1&limit=100'));
      return Object.fromEntries(rows.map((b) => [b.id, b.timezone ?? null]));
    },
  });
}

/** GET /tech/home's running job (B27): the caller's own started job that is
 *  not on hold. The timer is now − started_at − held_seconds. */
export interface RunningJob {
  kind: 'wo' | 'ticket';
  id: string;
  ref: string;
  title: string;
  building_id: string | null;
  site_name: string | null;
  started_at: string;
  held_seconds: number;
}
/** GET /tech/home (B1) — what the technician's shell reads on every page. */
export interface TechHome {
  running_job: RunningJob | null;
  checkin: OpenCheckin | null;
  sites: { id: string; name: string; timezone: string | null }[];
  /** C3 (R2 S1): the tiles, the next three of mine, the site's clock. */
  tiles?: { mine: number; take: number; done: number };
  next_up?: JobRow[];
  clock?: HomeClock;
  /** S5 adds these; absent, their cards render nothing. */
  handover?: unknown;
  shift_lead?: unknown;
}

export const TECH_HOME_KEY = ['tech', 'home'] as const;

/** GET /tech/home — maintenance:work. `enabled` is the caller's gate (the
 *  technician shell). Refreshed every minute and on focus; while offline the
 *  last answer stays, and `dataUpdatedAt` is the "data from HH:MM". */
export function useTechHome(enabled: boolean) {
  return useQuery<TechHome>({
    queryKey: TECH_HOME_KEY,
    queryFn: () => apiGet<TechHome>('/tech/home'),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Release 2, slice S1 — the one job list (B2), the job page (C6), home (C3)
// ---------------------------------------------------------------------------

export type JobKind = 'ticket' | 'wo' | 'inspection';

/** A job's hold (D16): the reason's words and since when. */
export interface JobHold { reason: string; label: string; since: string | null; note: string | null }

/** One row of GET /tech/jobs — the same shape for all three systems (D19). */
export interface JobRow {
  kind: JobKind;
  id: string;
  ref: string;
  title: string;
  site: string | null;
  site_id: string | null;
  site_timezone: string | null;
  asset: { id: string; name: string; code: string } | null;
  priority: 'critical' | 'high' | 'medium' | 'low' | null;
  status: string;
  status_label: string;
  wo_type: string | null;
  amc: boolean;
  due_at: string | null;
  /** Minutes until due; negative once late; null with no SLA or due date. */
  sla_left_min: number | null;
  hold: JobHold | null;
  running: boolean;
  accepted_at: string | null;
}

/** The C3 home's clock: greeting and date on the site's clock, with its label. */
export interface HomeClock {
  zone: string; label: string; site_name: string | null; now: string; date: string; greeting: string;
}

export type JobTab = 'mine' | 'take' | 'done';
/** The filter chips (C5): kinds, and PPM / AMC visits as work-order types. */
export type JobFilter = 'all' | 'ticket' | 'wo' | 'ppm' | 'inspection' | 'amc';

export function jobsQuery(tab: JobTab, filter: JobFilter): string {
  const q = new URLSearchParams({ tab });
  if (filter === 'ticket' || filter === 'wo' || filter === 'inspection') q.set('kind', filter);
  if (filter === 'ppm' || filter === 'amc') q.set('type', filter);
  return `/tech/jobs?${q.toString()}`;
}

export const TECH_JOBS_KEY = ['tech', 'jobs'] as const;
export const TECH_JOB_KEY = ['tech', 'job'] as const;

/** GET /tech/jobs — maintenance:work. */
export function useTechJobs(tab: JobTab, filter: JobFilter, enabled = true) {
  return useQuery<JobRow[]>({
    queryKey: [...TECH_JOBS_KEY, tab, filter],
    queryFn: () => apiGet<JobRow[]>(jobsQuery(tab, filter)),
    enabled,
    // NO SIGNAL (D8, R2 e2e F3): with the phone offline React Query would
    // PAUSE this read instead of failing it, so the "No signal — data from
    // HH:MM" fallback (which waits for a failed read) never showed, and a
    // filter not read before said "No jobs for you right now". Always send
    // it: offline it fails at once as no signal, and the page falls back to
    // what is saved on this phone.
    networkMode: 'always',
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export interface ActionState { allowed: boolean; why: string | null }
export type JobActionName = 'accept' | 'decline' | 'reached' | 'start' | 'hold' | 'resume';

export interface JobBanner {
  type: 'cancelled' | 'moved' | 'permit_expiring' | 'permit_expired';
  tone: 'red' | 'amber';
  text: string;
  permit_id?: string;
}

export interface JobHistoryEntry {
  id: string; action: string; what: string; by: string | null;
  received_at: string | null; client_at: string | null; detail: string | null;
}

/** GET /tech/jobs/{kind}/{id}. A job moved off me answers with
 *  `moved_away` and the banner only. */
export interface JobPage extends Partial<JobRow> {
  kind: JobKind; id: string; ref: string; title: string;
  moved_away?: boolean;
  description?: string | null;
  location_detail?: string | null;
  floor?: string | null;
  started_at?: string | null;
  reached_at?: string | null;
  reached_method?: string | null;
  voltage_class?: string | null;
  /** B37 (R3 S10): the safety check before electrical work — needed, done,
   *  or to be done again after a pause. Work orders only. */
  safety_check?: { required: boolean; done: boolean; again: boolean };
  /** B38: an emergency job's kind (its step list is in JobSafety). */
  emergency_kind?: 'power_failure' | 'lift_entrapment' | 'fire_panel' | null;
  banner: JobBanner | null;
  actions: Partial<Record<JobActionName, ActionState>>;
  running_other?: RunningJob | null;
  history: JobHistoryEntry[];
}

export function useTechJob(kind: string | undefined, id: string | undefined) {
  return useQuery<JobPage>({
    queryKey: [...TECH_JOB_KEY, kind, id],
    queryFn: () => apiGet<JobPage>(`/tech/jobs/${kind}/${id}`),
    enabled: !!kind && !!id,
    // Offline: fail as no signal (the saved job), never pause — see useTechJobs.
    networkMode: 'always',
    refetchOnWindowFocus: true,
  });
}

/** Every query that shows a job: the lists, the job page, home (tiles, next
 *  up) and the running bar (which reads home), and the alerts count. */
export function invalidateJobs(qc: { invalidateQueries: (f: { queryKey: readonly unknown[] }) => unknown }) {
  void qc.invalidateQueries({ queryKey: TECH_JOBS_KEY });
  void qc.invalidateQueries({ queryKey: TECH_JOB_KEY });
  void qc.invalidateQueries({ queryKey: TECH_HOME_KEY });
  void qc.invalidateQueries({ queryKey: ['notifications'] });
}
